// src/components/Business/Scheduling/DocumentPanel.jsx
// D6 — Business Web document surface. ADDITIVE: it drops into the EXISTING Scheduling appointment
// detail beside MoversJobPanel, and creates no navigation item, no dashboard and no second surface.
//
// THE BACKEND IS THE ONLY AUTHORITY. This panel decides nothing:
//   * whether a document is required          -> the appointment's server-stamped requirement
//   * what it is called                       -> the server-resolved friendly label
//   * what state it is in                     -> DocumentEngine, via the canonical projection
//   * which buttons exist                     -> server `allowed_actions`, never role or state
//                                                inspected here
//   * whether the booking may be confirmed    -> the scheduling authority's own allowed_actions
//
// It never infers state from a file existing, from a filename, from an upload having happened, or
// from the caller's role. It never constructs or signs a storage URL: viewing goes through M14's
// existing read route, which applies its own tenant check and its own security gate.

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { FileText, Eye, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DocumentPanel({ appointmentId, businessId, api, t, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  // A LOAD failure and an ACTION failure are different problems. A load failure means nothing can
  // be shown; an action failure means the document is still exactly where it was, and blanking the
  // section would take that context away from the operator at the moment they most need it.
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);          // 'view' | 'accepted' | 'rejected'
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    if (!appointmentId || !businessId) return;
    setLoading(true);
    setLoadError(null);
    setActionError(null);
    try {
      const resp = await api.get(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}` +
        `/document?business_id=${encodeURIComponent(businessId)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setDoc(resp.data || null);
    } catch (e) {
      // Truthful failure: the section says it could not be loaded rather than silently vanishing,
      // which would read as "no document required".
      setLoadError(e.message || 'error');
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [api, appointmentId, businessId]);

  useEffect(() => { load(); }, [load]);

  const view = useCallback(async () => {
    const mediaId = doc?.document?.media_id;
    if (!mediaId) return;
    setBusy('view');
    setActionError(null);
    try {
      // M14's EXISTING read authority. The client never builds or signs a storage URL, and the
      // server refuses anything that is not security-cleared.
      const resp = await api.get(`${BACKEND_URL}/api/v1/media/${encodeURIComponent(mediaId)}/url`);
      if (!resp.ok || !resp.data?.url) throw new Error(t('sched.doc.viewFailed'));
      window.open(resp.data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setActionError(e.message || t('sched.doc.viewFailed'));
    } finally {
      setBusy(null);
    }
  }, [api, doc, t]);

  const decide = useCallback(async (decision) => {
    setBusy(decision);
    setActionError(null);
    // Held so the catch can say something useful even if the throw happened before `decisionResp`
    // was ever assigned -- which is precisely the case that was previously invisible.
    let reached = 'start';
    try {
      // The client sends an INTENT. It never sends a state, a tenant, a customer or a scan verdict.
      reached = 'post';
      const decisionResp = await api.post(
        `${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}/document/decision`,
        { business_id: businessId, decision, reason: reason || null });
      reached = 'response';
      if (!decisionResp.ok) {
        throw new Error(decisionResp.data?.detail?.message || `HTTP ${decisionResp.status}`);
      }
      setRejecting(false);
      setReason('');
      // Re-read from the server rather than assuming the outcome. Nothing terminal is shown until
      // the canonical state says so.
      await load();
      if (typeof onChanged === 'function') onChanged();
    } catch (e) {
      // The response used to be named `resp`, and under minification it collided with the
      // translator in this very catch: `t(...)` resolved to the response object and calling it
      // threw a second time, replacing the real failure with a TypeError. The original error is
      // what an operator needs, so it is preserved and shown, and the fallback text is only used
      // when there is genuinely nothing to say.
      console.error('[D6] document decision failed at', reached, e);
      setActionError(e?.message || t('sched.doc.decisionFailed'));
    } finally {
      setBusy(null);
    }
  }, [api, appointmentId, businessId, reason, load, onChanged, t]);

  if (loading) {
    return (
      <div className="pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-400 flex items-center gap-1.5 pt-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('sched.doc.loading')}
        </div>
      </div>
    );
  }

  // No requirement -> no section at all. A booking that asks for nothing looks exactly as it did.
  if (!loadError && !doc?.document?.required) return null;

  const d = doc?.document || {};
  const actions = d.allowed_actions || {};

  return (
    <div className="pt-2 border-t border-gray-100">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1 pt-1">
        <FileText className="h-3.5 w-3.5" />{t('sched.doc.title')}
      </div>

      {loadError ? (
        <div className="flex items-center gap-2">
          <p className="text-xs text-red-600">{loadError}</p>
          <Button size="sm" variant="outline" onClick={load}>{t('sched.doc.retry')}</Button>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            {/* The friendly label and the state are BOTH server-resolved — no Clinic wording here. */}
            <span className="text-sm font-medium text-gray-800">{d.label}</span>
            <span className="text-xs text-gray-500">{d.status}</span>
          </div>

          {(actions.view_document || actions.accept_document || actions.reject_document) && (
            <div className="flex flex-wrap gap-2 pt-3">
              {actions.view_document && (
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={view}>
                  {busy === 'view'
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Eye className="h-4 w-4 mr-1" />}
                  {t('sched.doc.view')}
                </Button>
              )}
              {actions.accept_document && (
                <Button size="sm" className="bg-green-600 hover:bg-green-700"
                        disabled={busy !== null} onClick={() => decide('accepted')}>
                  {busy === 'accepted'
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  {t('sched.doc.accept')}
                </Button>
              )}
              {actions.reject_document && (
                <Button size="sm" variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={busy !== null} onClick={() => setRejecting(true)}>
                  <XCircle className="h-4 w-4 mr-1" />{t('sched.doc.reject')}
                </Button>
              )}
            </div>
          )}

          {rejecting && (
            <div className="pt-3 space-y-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder={t('sched.doc.reasonOptional')} />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                        disabled={busy !== null} onClick={() => decide('rejected')}>
                  {busy === 'rejected' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {t('sched.doc.confirmReject')}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy !== null}
                        onClick={() => { setRejecting(false); setReason(''); }}>
                  {t('sched.doc.cancelReject')}
                </Button>
              </div>
            </div>
          )}

          {actionError && <p className="text-xs text-red-600 pt-2">{actionError}</p>}

          {/* Why Confirm is unavailable, stated plainly. The server decides it; this only explains
              a gap the operator can already see, so it can never disagree with the server. */}
          {d.required && !d.satisfied && (
            <p className="text-[11px] text-gray-400 pt-2">{t('sched.doc.confirmBlocked')}</p>
          )}
        </>
      )}
    </div>
  );
}
